import { useState, useCallback, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { InputSwitch } from 'primereact/inputswitch';
import { Tooltip } from 'primereact/tooltip';
import {
  useTenant,
  useNamespaces,
  ThemeSelector,
  useBranding,
  useOptionalPermissions,
  allowsNamespaceList,
} from '@authifi/clique-react-ui';
import { SignOutButton } from '@authifi/clique-react-ui/auth';
import { SdkDocsPanel } from '../components/SdkDocsPanel';
import { useDemoNamespaceFilter } from '../demo-namespace-filter';
import { useDemoPermissionScope } from '../demo-permission-scope';

type ContentTab = 'data' | 'sdk';

const navItems = [
  { to: '/', label: 'Dashboard', icon: 'pi pi-home' },
  { to: '/groups', label: 'Groups', icon: 'pi pi-users' },
  { to: '/users', label: 'Users', icon: 'pi pi-user' },
  { to: '/identity-providers', label: 'Identity Providers', icon: 'pi pi-key' },
  { to: '/login-pages', label: 'Login Pages', icon: 'pi pi-file-edit' },
  { to: '/email-templates', label: 'Email Templates', icon: 'pi pi-envelope' },
  { to: '/umrs-roles', label: 'UMRS Roles', icon: 'pi pi-shield' },
  { to: '/roles', label: 'Roles', icon: 'pi pi-shield' },
  { to: '/permissions', label: 'Permissions', icon: 'pi pi-lock' },
  { to: '/resource-servers', label: 'Resource Servers', icon: 'pi pi-server' },
  { to: '/playground', label: 'Playground', icon: 'pi pi-palette' },
  { to: '/profile', label: 'Profile', icon: 'pi pi-id-card' },
];

const MIN_SIDEBAR_WIDTH = 48;
const MAX_SIDEBAR_WIDTH = 220;
const DEFAULT_SIDEBAR_WIDTH = 200;

export function AppLayout() {
  const auth = useAuth();
  const location = useLocation();
  const { tenantId } = useTenant();
  const { appName, logo } = useBranding();
  const namespaceFilter = useDemoNamespaceFilter();
  const permissionScope = useDemoPermissionScope();
  const permissions = useOptionalPermissions();
  const canListNamespaces = allowsNamespaceList(permissions?.hasScope);
  const { data: namespaces } = useNamespaces(
    { where: { id: namespaceFilter.namespaceId } },
    { enabled: canListNamespaces },
  );
  const resolvedNamespaceName = namespaces?.find(
    (ns) => ns.id === namespaceFilter.namespaceId,
  )?.name;
  const namespaceLabel = resolvedNamespaceName ?? `id: ${namespaceFilter.namespaceId}`;
  const namespaceIntro = resolvedNamespaceName
    ? `"${resolvedNamespaceName}" is a namespace — an app-specific bucket for users, groups, roles, and resource servers.`
    : 'A namespace is an app-specific bucket for users, groups, roles, and resource servers.';
  const namespaceFilterHint = namespaceFilter.enabled
    ? `${namespaceIntro} Turn off to list the whole tenant.`
    : `${namespaceIntro} Turn on to show only those records.`;
  const permissionScopeHint = permissionScope.enabled
    ? `PermissionList and the role picker show only permissions for "${permissionScope.identifier}". Turn off to list every tenant permission (or, with Namespace filter on, those on that namespace's resource servers).`
    : `Turn on to show only permissions for "${permissionScope.identifier}". With Namespace filter on, the unscoped list is limited to that namespace's resource servers.`;
  const user = auth.user?.profile;
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [activeTab, setActiveTab] = useState<ContentTab>('data');
  const isResizing = useRef(false);

  // Default back to the data view whenever the route changes so a page never
  // opens on the docs tab.
  useEffect(() => {
    setActiveTab('data');
  }, [location.pathname]);

  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, moveEvent.clientX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  return (
    <div
      className="flex h-screen"
      style={{ backgroundColor: 'var(--clique-bg-subtle)', color: 'var(--clique-fg)' }}
    >
      {/* Sidebar */}
      <aside
        className={`flex flex-col shrink-0 overflow-hidden ${sidebarOpen ? '' : 'w-0'}`}
        style={
          sidebarOpen
            ? {
                width: sidebarWidth,
                backgroundColor: 'var(--clique-sidebar-bg)',
                color: 'var(--clique-sidebar-fg)',
              }
            : undefined
        }
      >
        <div
          className="p-5 whitespace-nowrap"
          style={{ borderBottom: '1px solid var(--clique-sidebar-border)' }}
        >
          <h1 className="flex items-center gap-2 text-xl font-bold tracking-tight">
            {logo}
            {appName ?? 'Clique'}
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--clique-sidebar-fg-muted)' }}>
            Tenant: {tenantId}
          </p>
        </div>

        <nav className="flex-1 py-4">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 text-sm transition-colors whitespace-nowrap ${
                  isActive ? 'border-r-3' : ''
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      backgroundColor: 'var(--clique-sidebar-active)',
                      color: 'var(--clique-sidebar-fg)',
                      borderColor: 'var(--clique-sidebar-active-accent)',
                    }
                  : { color: 'var(--clique-sidebar-fg-muted)' }
              }
            >
              <i aria-hidden="true" className={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div
          className="p-4 text-xs whitespace-nowrap"
          style={{
            borderTop: '1px solid var(--clique-sidebar-border)',
            color: 'var(--clique-sidebar-fg-muted)',
          }}
        >
          v0.1.0
        </div>
      </aside>

      {/* Resize handle */}
      {sidebarOpen && (
        <div
          className="w-1 shrink-0 cursor-col-resize transition-colors"
          style={{ backgroundColor: 'var(--clique-sidebar-bg)' }}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
        />
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header
          className="h-14 flex items-center justify-between px-6 shrink-0"
          style={{
            backgroundColor: 'var(--clique-bg)',
            borderBottom: '1px solid var(--clique-border)',
          }}
        >
          <button
            type="button"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="transition-colors cursor-pointer"
            style={{ color: 'var(--clique-fg-secondary)' }}
            aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <i aria-hidden="true" className="pi pi-bars text-lg" />
          </button>

          <div className="flex items-center gap-3">
            <Tooltip
              target=".demo-namespace-filter, .demo-permission-scope"
              position="bottom"
              showDelay={300}
            />
            <label
              htmlFor="demo-namespace-filter"
              className="demo-namespace-filter flex items-center gap-2 text-sm cursor-pointer"
              style={{ color: 'var(--clique-fg-muted)' }}
              data-pr-tooltip={namespaceFilterHint}
            >
              <InputSwitch
                inputId="demo-namespace-filter"
                checked={namespaceFilter.enabled}
                onChange={(e) => namespaceFilter.setEnabled(e.value ?? false)}
              />
              Namespace filter (
              <span
                className="inline-block max-w-[10rem] truncate align-bottom"
                title={namespaceLabel}
              >
                {namespaceLabel}
              </span>
              )
            </label>
            <label
              htmlFor="demo-permission-scope"
              className="demo-permission-scope flex items-center gap-2 text-sm cursor-pointer"
              style={{ color: 'var(--clique-fg-muted)' }}
              data-pr-tooltip={permissionScopeHint}
            >
              <InputSwitch
                inputId="demo-permission-scope"
                checked={permissionScope.enabled}
                onChange={(e) => permissionScope.setEnabled(e.value ?? false)}
              />
              Permission scope (
              <span
                className="inline-block max-w-[10rem] truncate align-bottom"
                title={permissionScope.identifier}
              >
                {permissionScope.identifier}
              </span>
              )
            </label>
            <ThemeSelector className="text-sm" />
            {user && (
              <>
                <span className="text-sm" style={{ color: 'var(--clique-fg-muted)' }}>
                  {user.email || user.name || user.preferred_username || user.sub}
                </span>
                <SignOutButton label="Logout" text className="text-sm" />
              </>
            )}
          </div>
        </header>

        {/* Content tabs */}
        <div
          className="flex items-center gap-1 px-6 shrink-0"
          style={{
            backgroundColor: 'var(--clique-bg)',
            borderBottom: '1px solid var(--clique-border)',
          }}
          role="tablist"
          aria-label="Page view"
        >
          <TabButton
            label="Data"
            icon="pi pi-table"
            active={activeTab === 'data'}
            onClick={() => setActiveTab('data')}
          />
          <TabButton
            label="Integration help"
            icon="pi pi-code"
            active={activeTab === 'sdk'}
            onClick={() => setActiveTab('sdk')}
          />
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-6">
          <div hidden={activeTab !== 'data'}>
            <Outlet />
          </div>
          {activeTab === 'sdk' && <SdkDocsPanel pathname={location.pathname} />}
        </main>
      </div>
    </div>
  );
}

function TabButton({
  label,
  icon,
  active,
  onClick,
}: {
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="inline-flex items-center gap-2 px-4 py-3 text-sm transition-colors cursor-pointer"
      style={
        active
          ? {
              color: 'var(--clique-primary)',
              borderBottom: '2px solid var(--clique-primary)',
              fontWeight: 'var(--clique-font-weight-semibold)' as React.CSSProperties['fontWeight'],
            }
          : { color: 'var(--clique-fg-secondary)', borderBottom: '2px solid transparent' }
      }
    >
      <i aria-hidden="true" className={icon} />
      {label}
    </button>
  );
}
